/**
 * PricingScaleTable
 *
 * Dynamic pricing scale table that displays pricing tiers based on user count.
 * Allows users to configure the increment and maximum member count to generate
 * a customized pricing table showing costs across different user counts.
 *
 * @see src/modules/RetoolModule/components/PricingCalculatorForm.tsx
 * @see src/modules/RetoolModule/types/pricingCalculator.ts
 */

import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import {
  Box,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  ButtonGroup,
  Chip,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  calculateSeatPricePerMonth,
  calculatePlatformFee,
  calculateSsoFeePerUser,
  calculateTenantFee,
  calculateGovCloudFee,
  calculateIntegrationRecurringFee,
  calculateAiForecastingFee,
  calculateNonPeopleResourcesFee,
  calculateProfileBuildingFee,
  calculateSkillsTaggingFee,
  calculateSolutionsSupportFee,
  calculateWhiteLabelAdditionalCost,
  calculateDirectDatabaseFee,
  calculateIsolatedDatabaseFee,
  isDedicatedTenant,
  getPlanTypeLabel,
  PlanType,
  TenantType,
} from '../types/pricingCalculator';

const ScaleTableContainer = styled(Box)`
  margin-top: 24px;
  margin-bottom: 24px;
`;

const ConfigRow = styled(Box)`
  display: flex;
  gap: 24px;
  margin-top: 16px;
  margin-bottom: 24px;
  align-items: flex-start;
  justify-content: flex-start;
`;

const StyledTableContainer = styled(TableContainer)`
  .MuiTableCell-head {
    background-color: ${({ theme }) =>
      theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.95)' : '#f5f5f5'};
    font-weight: 600;
    position: sticky;
    top: 0;
    z-index: 1;
    border-bottom: 2px solid
      ${({ theme }) => (theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.2)' : '#e0e0e0')};
  }
`;

const StyledTableRow = styled(TableRow)<{ $isCurrentRow?: boolean; $isTierBoundary?: boolean }>`
  &:nth-of-type(even) {
    background-color: ${({ theme }) =>
      theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.5)' : '#fafafa'};
  }

  &:hover {
    background-color: ${({ theme }) =>
      theme.palette.mode === 'dark' ? 'rgba(51, 65, 85, 0.8)' : '#f0f0f0'};
  }

  ${({ $isCurrentRow, theme }) =>
    $isCurrentRow &&
    `
    background-color: ${theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)'} !important;
    border-left: 3px solid ${theme.palette.primary.main};

    & td {
      font-weight: 600;
    }
  `}

  ${({ $isTierBoundary, theme }) =>
    $isTierBoundary &&
    `
    border-top: 2px dashed ${theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.3)' : 'rgba(100, 116, 139, 0.3)'};
  `}
`;

const NumericCell = styled(TableCell)`
  text-align: right !important;
  font-family: 'Roboto Mono', monospace;
`;

const HeaderCell = styled(TableCell)`
  text-align: right !important;
  font-weight: 600 !important;
`;

const ResultsText = styled(Typography)`
  text-align: center;
  padding: 16px;
  color: ${({ theme }) => (theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.8)' : '#666')};
  font-style: italic;
`;

// Helper to calculate platform tier from user count
const getPlatformTier = (userCount: number): number => {
  if (userCount <= 25) return 1;
  if (userCount <= 51) return 2;
  if (userCount <= 101) return 3;
  if (userCount <= 151) return 4;
  if (userCount <= 201) return 5;
  if (userCount <= 251) return 6;
  if (userCount <= 301) return 7;
  if (userCount <= 351) return 8;
  if (userCount <= 401) return 9;
  if (userCount <= 451) return 10;
  if (userCount <= 501) return 11;
  if (userCount <= 601) return 12;
  if (userCount <= 701) return 13;
  if (userCount <= 801) return 14;
  if (userCount <= 901) return 15;
  if (userCount <= 1001) return 16;
  // Continue pattern for higher tiers
  return Math.ceil((userCount - 1001) / 250) + 16;
};

interface PricingScaleTableProps {
  subscriptionDuration?: number;
  numberOfMembers?: number;
  planType?: PlanType;
  isItCustomer?: boolean;
  tenantType?: TenantType;
  govCloud?: boolean;
  securityPackageSso?: boolean;
  discountOnSecurityPackageSso?: number;
  pricePerMember?: number;
  discountOnMemberPrice?: number;
  discountOnPlatformFee?: number;
  discountOnTenant?: number;
  discountOnGovCloudFee?: number;
  // Direct Database props
  directDatabaseIntegration?: boolean;
  discountOnDirectDatabase?: number;
  // Isolated Database props
  isolatedDatabase?: boolean;
  discountOnIsolatedDatabase?: number;
  // Per-integration data
  selectedIntegrations?: string[];
  integrationRecurringDiscounts?: Record<string, number>;
  enable4xDataTransfer?: boolean;
  // Add-on props
  aiForecasting?: boolean;
  discountOnAiForecasting?: number;
  nonPeopleResources?: boolean;
  discountOnNonPeopleResources?: number;
  profileBuilding?: boolean;
  discountOnProfileBuilding?: number;
  skillsTagging?: boolean;
  discountOnSkillsTagging?: number;
  solutionsSupport?: boolean;
  solutionsSupportHours?: number;
  enableWhiteLabel?: boolean;
}

interface PricingRow {
  userCount: number;
  pricePerSeat: number;
  recurringSubscriptionFee: number;
  recurringAnnualPlatformFee: number;
  recurringSsoAnnualFee: number;
  recurringIntegrationFee: number;
  recurringDirectDatabaseFee: number;
  recurringIsolatedDatabaseFee: number;
  recurringTenantFee: number;
  recurringGovCloudFee: number;
  recurringAddonFee: number;
  totalRecurringFee: number;
  costPerUser: number;
  platformTier: number;
}

type ScaleConfigSummaryProps = {
  subscriptionDuration: number;
  integrationCount: number;
  planType?: PlanType;
  directDatabaseIntegration: boolean;
  isolatedDatabase: boolean;
  tenantType: TenantType;
  govCloud: boolean;
  securityPackageSso: boolean;
};

/** Read-only recap of the configuration the scale table was generated from. */
const ScaleConfigSummary: React.FC<ScaleConfigSummaryProps> = ({
  subscriptionDuration,
  integrationCount,
  planType,
  directDatabaseIntegration,
  isolatedDatabase,
  tenantType,
  govCloud,
  securityPackageSso,
}) => (
  <Box>
    <Typography variant="body2" color="textSecondary">
      Subscription: {subscriptionDuration} months
    </Typography>
    <Typography variant="body2" color="textSecondary">
      Integrations: {integrationCount}
    </Typography>
    {planType && (
      <Typography variant="body2" color="textSecondary">
        Plan: {getPlanTypeLabel(planType)}
      </Typography>
    )}
    {directDatabaseIntegration && !isDedicatedTenant(tenantType) && (
      <Typography variant="body2" color="textSecondary">
        Direct Database: Yes
      </Typography>
    )}
    {isolatedDatabase && !directDatabaseIntegration && !isDedicatedTenant(tenantType) && (
      <Typography variant="body2" color="textSecondary">
        Isolated Database: Yes
      </Typography>
    )}
    {tenantType === TenantType.SINGLE && (
      <Typography variant="body2" color="textSecondary">
        Single Tenant: Yes
      </Typography>
    )}
    {tenantType === TenantType.ENTERPRISE && (
      <Typography variant="body2" color="textSecondary">
        Enterprise Tenant: Yes
      </Typography>
    )}
    {govCloud && (
      <Typography variant="body2" color="textSecondary">
        Gov Cloud: Yes
      </Typography>
    )}
    {securityPackageSso && (
      <Typography variant="body2" color="textSecondary">
        SSO: Enabled
      </Typography>
    )}
  </Box>
);

/**
 * Applies every prop default in one place. Kept as parameter defaults (not an object
 * spread) so an explicitly-passed `undefined` still falls back to the default, exactly
 * as the inline destructuring did.
 */
const resolveScaleTableProps = ({
  subscriptionDuration = 1,
  numberOfMembers = 10,
  planType,
  isItCustomer = false,
  tenantType = TenantType.SHARED,
  govCloud = false,
  securityPackageSso = false,
  discountOnSecurityPackageSso = 0,
  pricePerMember,
  discountOnMemberPrice = 0,
  discountOnPlatformFee = 0,
  discountOnTenant = 0,
  discountOnGovCloudFee = 0,
  // Direct Database props
  directDatabaseIntegration = false,
  discountOnDirectDatabase = 0,
  // Isolated Database props
  isolatedDatabase = false,
  discountOnIsolatedDatabase = 0,
  selectedIntegrations = [],
  integrationRecurringDiscounts = {},
  enable4xDataTransfer = false,
  // Add-on props
  aiForecasting = false,
  discountOnAiForecasting = 0,
  nonPeopleResources = false,
  discountOnNonPeopleResources = 0,
  profileBuilding = false,
  discountOnProfileBuilding = 0,
  skillsTagging = false,
  discountOnSkillsTagging = 0,
  solutionsSupport = false,
  solutionsSupportHours = 0,
  enableWhiteLabel = false,
}: PricingScaleTableProps) => ({
  subscriptionDuration,
  numberOfMembers,
  planType,
  isItCustomer,
  tenantType,
  govCloud,
  securityPackageSso,
  discountOnSecurityPackageSso,
  pricePerMember,
  discountOnMemberPrice,
  discountOnPlatformFee,
  discountOnTenant,
  discountOnGovCloudFee,
  directDatabaseIntegration,
  discountOnDirectDatabase,
  isolatedDatabase,
  discountOnIsolatedDatabase,
  selectedIntegrations,
  integrationRecurringDiscounts,
  enable4xDataTransfer,
  aiForecasting,
  discountOnAiForecasting,
  nonPeopleResources,
  discountOnNonPeopleResources,
  profileBuilding,
  discountOnProfileBuilding,
  skillsTagging,
  discountOnSkillsTagging,
  solutionsSupport,
  solutionsSupportHours,
  enableWhiteLabel,
});

/**
 * The resolved props a single row depends on. Excludes the three that only drive
 * the surrounding table (row range and the config recap), so the memo below can
 * hand over exactly its own dependencies and keep its dependency array unchanged.
 */
type PricingRowInputs = Omit<
  ReturnType<typeof resolveScaleTableProps>,
  'isItCustomer' | 'numberOfMembers' | 'pricePerMember'
>;

/**
 * One row of the scale table. Pure: every input is a resolved prop, so the memo
 * below stays a thin loop over user counts.
 */
const buildPricingRow = (
  currentCount: number,
  {
    planType,
    securityPackageSso,
    tenantType,
    selectedIntegrations,
    integrationRecurringDiscounts,
    govCloud,
    enable4xDataTransfer,
    subscriptionDuration,
    discountOnMemberPrice,
    discountOnSecurityPackageSso,
    discountOnPlatformFee,
    directDatabaseIntegration,
    discountOnDirectDatabase,
    isolatedDatabase,
    discountOnIsolatedDatabase,
    discountOnTenant,
    discountOnGovCloudFee,
    aiForecasting,
    discountOnAiForecasting,
    nonPeopleResources,
    discountOnNonPeopleResources,
    profileBuilding,
    discountOnProfileBuilding,
    skillsTagging,
    discountOnSkillsTagging,
    solutionsSupport,
    solutionsSupportHours,
    enableWhiteLabel,
  }: PricingRowInputs,
): PricingRow => {
  const pricePerSeat = calculateSeatPricePerMonth(currentCount, planType);
  const platformFeeData = calculatePlatformFee(currentCount, planType);
  const ssoFeePerUser = securityPackageSso ? calculateSsoFeePerUser(currentCount) : 0;

  const onDedicatedTenant = isDedicatedTenant(tenantType);

  // Calculate total integration fee by summing each integration's fee with its individual discount
  let recurringIntegrationFee = 0;
  selectedIntegrations.forEach((integrationKey) => {
    const perIntegrationDiscount = integrationRecurringDiscounts[integrationKey] || 0;
    const integrationFee = calculateIntegrationRecurringFee(
      integrationKey,
      currentCount,
      govCloud,
      onDedicatedTenant,
      enable4xDataTransfer,
      perIntegrationDiscount,
    );
    // Convert annual fee to monthly and multiply by subscription duration
    recurringIntegrationFee += (integrationFee / 12) * subscriptionDuration;
  });

  // Always use calculated pricePerSeat for scale table to show correct tier pricing
  // Apply discount on member price
  const discountedPricePerSeat = pricePerSeat * (1 - discountOnMemberPrice / 100);

  // Calculate recurring fees based on subscription duration with discounts applied
  const recurringSubscriptionFee = currentCount * discountedPricePerSeat * subscriptionDuration;

  // Apply SSO discount to SSO fee
  const recurringSsoAnnualFee =
    currentCount * ssoFeePerUser * subscriptionDuration * (1 - discountOnSecurityPackageSso / 100);

  // Apply discount on platform fee
  const recurringAnnualPlatformFee =
    platformFeeData.platformFee * (1 - discountOnPlatformFee / 100);

  // Calculate Direct Database Fee (if enabled) with discount applied
  // Direct Database is mutually exclusive with dedicated tenant tiers
  // Note: discount is passed to calculateDirectDatabaseFee which ensures minimum fee is maintained
  const recurringDirectDatabaseFee =
    directDatabaseIntegration && !onDedicatedTenant
      ? calculateDirectDatabaseFee(currentCount, subscriptionDuration, discountOnDirectDatabase)
      : 0;

  // Calculate Isolated Database Fee (if enabled and not using Direct Database or dedicated tenant)
  // Isolated Database is included in Direct Database and dedicated tenant tiers
  const recurringIsolatedDatabaseFee =
    isolatedDatabase && !directDatabaseIntegration && !onDedicatedTenant
      ? calculateIsolatedDatabaseFee(currentCount) *
        subscriptionDuration *
        (1 - discountOnIsolatedDatabase / 100)
      : 0;

  // Calculate dedicated tenant fee (Single or Enterprise) with discount applied
  const recurringTenantFee = onDedicatedTenant
    ? calculateTenantFee(tenantType, currentCount, subscriptionDuration) *
      (1 - discountOnTenant / 100)
    : 0;

  // Calculate Gov Cloud Fee (if enabled) with discount applied
  // Gov Cloud fee = 20% of (member costs + SSO costs + dedicated tenant/isolated DB fee + integration fees)
  const recurringGovCloudFee = govCloud
    ? calculateGovCloudFee(
        currentCount,
        pricePerSeat,
        discountOnMemberPrice,
        subscriptionDuration,
        securityPackageSso,
        isolatedDatabase && !directDatabaseIntegration && !onDedicatedTenant,
        tenantType,
        selectedIntegrations.length,
      ) *
      (1 - discountOnGovCloudFee / 100)
    : 0;

  // Calculate Add-on Fees with discounts applied
  // AI Forecasting: 35% × (Total Annual Member Fees + Total Annual Integration Fees) / 12 × duration
  const aiForecastingFee = aiForecasting
    ? calculateAiForecastingFee(
        currentCount,
        pricePerSeat,
        discountOnMemberPrice,
        subscriptionDuration,
        (recurringIntegrationFee * 12) / subscriptionDuration, // Convert back to annual for calculation
      ) *
      (1 - discountOnAiForecasting / 100)
    : 0;

  // Non-People Resources: 50% of Discounted License Cost * users * duration
  const nonPeopleResourcesFee = nonPeopleResources
    ? calculateNonPeopleResourcesFee(
        currentCount,
        pricePerSeat,
        discountOnMemberPrice,
        subscriptionDuration,
      ) *
      (1 - discountOnNonPeopleResources / 100)
    : 0;

  // Profile Building: $10 * users * duration
  const profileBuildingFee = profileBuilding
    ? calculateProfileBuildingFee(currentCount, subscriptionDuration) *
      (1 - discountOnProfileBuilding / 100)
    : 0;

  // Skills Tagging: $5 * users * duration
  const skillsTaggingFee = skillsTagging
    ? calculateSkillsTaggingFee(currentCount, subscriptionDuration) *
      (1 - discountOnSkillsTagging / 100)
    : 0;

  // Solutions Support: hours * $200 (no discount field for this)
  const solutionsSupportFee = solutionsSupport
    ? calculateSolutionsSupportFee(solutionsSupportHours)
    : 0;

  // White-Label: 20% increase in per seat cost (no discount field)
  const whiteLabelFee = enableWhiteLabel
    ? calculateWhiteLabelAdditionalCost(
        currentCount,
        pricePerSeat,
        discountOnMemberPrice,
        subscriptionDuration,
      )
    : 0;

  // Total Add-on Fee
  const recurringAddonFee =
    aiForecastingFee +
    nonPeopleResourcesFee +
    profileBuildingFee +
    skillsTaggingFee +
    solutionsSupportFee +
    whiteLabelFee;

  const totalRecurringFee =
    recurringSubscriptionFee +
    recurringAnnualPlatformFee +
    recurringSsoAnnualFee +
    recurringIntegrationFee +
    recurringDirectDatabaseFee +
    recurringIsolatedDatabaseFee +
    recurringTenantFee +
    recurringGovCloudFee +
    recurringAddonFee;

  // Calculate cost per user per month (total / users / months)
  const costPerUser =
    currentCount > 0 ? totalRecurringFee / currentCount / subscriptionDuration : 0;

  return {
    userCount: currentCount,
    pricePerSeat: discountedPricePerSeat,
    recurringSubscriptionFee,
    recurringAnnualPlatformFee,
    recurringSsoAnnualFee,
    recurringIntegrationFee,
    recurringDirectDatabaseFee,
    recurringIsolatedDatabaseFee,
    recurringTenantFee,
    recurringGovCloudFee,
    recurringAddonFee,
    totalRecurringFee,
    costPerUser,
    platformTier: getPlatformTier(currentCount),
  };
};

export const PricingScaleTable: React.FC<PricingScaleTableProps> = (props) => {
  const {
    subscriptionDuration,
    numberOfMembers,
    planType,
    isItCustomer,
    tenantType,
    govCloud,
    securityPackageSso,
    discountOnSecurityPackageSso,
    pricePerMember,
    discountOnMemberPrice,
    discountOnPlatformFee,
    discountOnTenant,
    discountOnGovCloudFee,
    directDatabaseIntegration,
    discountOnDirectDatabase,
    isolatedDatabase,
    discountOnIsolatedDatabase,
    selectedIntegrations,
    integrationRecurringDiscounts,
    enable4xDataTransfer,
    aiForecasting,
    discountOnAiForecasting,
    nonPeopleResources,
    discountOnNonPeopleResources,
    profileBuilding,
    discountOnProfileBuilding,
    skillsTagging,
    discountOnSkillsTagging,
    solutionsSupport,
    solutionsSupportHours,
    enableWhiteLabel,
  } = resolveScaleTableProps(props);

  const theme = useTheme();
  const [incrementMembers, setIncrementMembers] = useState<number>(50);
  const [maxMembers, setMaxMembers] = useState<number>(1000);
  const [activePreset, setActivePreset] = useState<string | null>(null);

  // Maximum rows to prevent performance issues
  const MAX_ROWS = 300;

  // Preset configurations for quick scale viewing
  const applyPreset = (preset: string) => {
    setActivePreset(preset);
    switch (preset) {
      case 'around':
        // Show ±100 around current member count
        setIncrementMembers(25);
        setMaxMembers(Math.max(numberOfMembers + 100, 200));
        break;
      case 'growth':
        // Show growth scenarios: current, +25%, +50%, +100%
        setIncrementMembers(Math.max(Math.round(numberOfMembers * 0.25), 10));
        setMaxMembers(numberOfMembers * 2);
        break;
      case 'tiers':
        // Show tier boundaries
        setIncrementMembers(50);
        setMaxMembers(1000);
        break;
      case 'full':
        // Full range view
        setIncrementMembers(100);
        setMaxMembers(2000);
        break;
      default:
        break;
    }
  };

  // Check if a user count is at or just past a tier boundary
  const isTierBoundary = (userCount: number, prevUserCount: number | null): boolean => {
    if (prevUserCount === null) return false;
    const currentTier = getPlatformTier(userCount);
    const prevTier = getPlatformTier(prevUserCount);
    return currentTier !== prevTier;
  };

  // Generate pricing rows based on increment and max
  const pricingRows = useMemo((): PricingRow[] => {
    if (incrementMembers <= 0 || maxMembers <= 0) return [];

    const rowInputs: PricingRowInputs = {
      planType,
      securityPackageSso,
      tenantType,
      selectedIntegrations,
      integrationRecurringDiscounts,
      govCloud,
      enable4xDataTransfer,
      subscriptionDuration,
      discountOnMemberPrice,
      discountOnSecurityPackageSso,
      discountOnPlatformFee,
      directDatabaseIntegration,
      discountOnDirectDatabase,
      isolatedDatabase,
      discountOnIsolatedDatabase,
      discountOnTenant,
      discountOnGovCloudFee,
      aiForecasting,
      discountOnAiForecasting,
      nonPeopleResources,
      discountOnNonPeopleResources,
      profileBuilding,
      discountOnProfileBuilding,
      skillsTagging,
      discountOnSkillsTagging,
      solutionsSupport,
      solutionsSupportHours,
      enableWhiteLabel,
    };

    const rows: PricingRow[] = [];
    let currentCount = numberOfMembers;

    // Ensure we use the larger of maxMembers or numberOfMembers to always show at least the starting row
    const effectiveMax = Math.max(maxMembers, numberOfMembers);

    while (currentCount <= effectiveMax && rows.length < MAX_ROWS) {
      rows.push(buildPricingRow(currentCount, rowInputs));

      currentCount += incrementMembers;
    }

    return rows;
  }, [
    numberOfMembers,
    incrementMembers,
    maxMembers,
    subscriptionDuration,
    selectedIntegrations,
    integrationRecurringDiscounts,
    enable4xDataTransfer,
    planType,
    tenantType,
    govCloud,
    securityPackageSso,
    discountOnSecurityPackageSso,
    pricePerMember,
    discountOnMemberPrice,
    discountOnPlatformFee,
    discountOnTenant,
    discountOnGovCloudFee,
    // Direct Database dependencies
    directDatabaseIntegration,
    discountOnDirectDatabase,
    // Isolated Database dependencies
    isolatedDatabase,
    discountOnIsolatedDatabase,
    // Add-on dependencies
    aiForecasting,
    discountOnAiForecasting,
    nonPeopleResources,
    discountOnNonPeopleResources,
    profileBuilding,
    discountOnProfileBuilding,
    skillsTagging,
    discountOnSkillsTagging,
    solutionsSupport,
    solutionsSupportHours,
    enableWhiteLabel,
  ]);

  const handleIncrementChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const value = parseInt(event.target.value) || 0;
    setIncrementMembers(Math.max(10, value));
  };

  const handleMaxChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = parseInt(event.target.value) || 0;
    setMaxMembers(Math.max(1, value));
  };

  const formatCurrency = (value: number): string => {
    return (
      '$' +
      value.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
    );
  };

  const formatPrice = (value: number): string => {
    return (
      '$' +
      value.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  };

  return (
    <ScaleTableContainer>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" style={{ fontWeight: 600 }}>
          Pricing Scale Table
        </Typography>
        <Chip
          label={`Current: ${numberOfMembers.toLocaleString()} members`}
          color="primary"
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
      </Box>

      {/* Quick Preset Buttons */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          Quick Views:
        </Typography>
        <ButtonGroup size="small" variant="outlined">
          <Tooltip title={`Show ${numberOfMembers} ± 100 members in small increments`}>
            <Button
              onClick={() => applyPreset('around')}
              variant={activePreset === 'around' ? 'contained' : 'outlined'}
            >
              Around Current
            </Button>
          </Tooltip>
          <Tooltip title="Show growth scenarios: current, +25%, +50%, +100%">
            <Button
              onClick={() => applyPreset('growth')}
              variant={activePreset === 'growth' ? 'contained' : 'outlined'}
            >
              Growth Scenarios
            </Button>
          </Tooltip>
          <Tooltip title="Show all tier boundaries for platform fees">
            <Button
              onClick={() => applyPreset('tiers')}
              variant={activePreset === 'tiers' ? 'contained' : 'outlined'}
            >
              Tier Boundaries
            </Button>
          </Tooltip>
          <Tooltip title="Full range view up to 2000 members">
            <Button
              onClick={() => applyPreset('full')}
              variant={activePreset === 'full' ? 'contained' : 'outlined'}
            >
              Full Range
            </Button>
          </Tooltip>
        </ButtonGroup>
      </Box>

      <ConfigRow>
        <TextField
          label="Increment of Members"
          type="number"
          value={incrementMembers}
          onChange={(e) => {
            handleIncrementChange(e);
            setActivePreset(null);
          }}
          inputProps={{ min: 10, step: 10 }}
          style={{ width: '200px' }}
          helperText="Minimum increment is 10 members"
          size="small"
        />
        <TextField
          label="Maximum Members"
          type="number"
          value={maxMembers}
          onChange={(e) => {
            handleMaxChange(e);
            setActivePreset(null);
          }}
          inputProps={{ min: 10 }}
          style={{ width: '200px' }}
          size="small"
        />
        <ScaleConfigSummary
          subscriptionDuration={subscriptionDuration}
          integrationCount={selectedIntegrations.length}
          planType={planType}
          directDatabaseIntegration={directDatabaseIntegration}
          isolatedDatabase={isolatedDatabase}
          tenantType={tenantType}
          govCloud={govCloud}
          securityPackageSso={securityPackageSso}
        />
      </ConfigRow>
      <Typography variant="body2" color="textSecondary" sx={{ marginBottom: 2 }}>
        Note: The prices displayed reflect the selected configuration options above.
        {isItCustomer && ' IT customer pricing may differ from standard rates.'}
      </Typography>

      {pricingRows.length > 0 ? (
        <>
          <Paper elevation={1}>
            <StyledTableContainer>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <HeaderCell align="center">User Count</HeaderCell>
                    <HeaderCell align="center">Tier</HeaderCell>
                    <HeaderCell>Price/Seat</HeaderCell>
                    <HeaderCell>Member Fee</HeaderCell>
                    <HeaderCell>Platform Fee</HeaderCell>
                    {securityPackageSso && <HeaderCell>SSO Fee</HeaderCell>}
                    <HeaderCell>Integration Fee</HeaderCell>
                    {directDatabaseIntegration && !isDedicatedTenant(tenantType) && (
                      <HeaderCell>Direct DB Fee</HeaderCell>
                    )}
                    {isolatedDatabase &&
                      !directDatabaseIntegration &&
                      !isDedicatedTenant(tenantType) && <HeaderCell>Isolated DB Fee</HeaderCell>}
                    {tenantType === TenantType.SINGLE && <HeaderCell>Single Tenant</HeaderCell>}
                    {tenantType === TenantType.ENTERPRISE && (
                      <HeaderCell>Enterprise Tenant</HeaderCell>
                    )}
                    {govCloud && <HeaderCell>Gov Cloud</HeaderCell>}
                    {(aiForecasting ||
                      nonPeopleResources ||
                      profileBuilding ||
                      skillsTagging ||
                      solutionsSupport ||
                      enableWhiteLabel) && <HeaderCell>Add-ons</HeaderCell>}
                    <HeaderCell>Total</HeaderCell>
                    <HeaderCell sx={{ backgroundColor: alpha(theme.palette.success.main, 0.1) }}>
                      Cost/User/Mo
                    </HeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pricingRows.map((row, index) => {
                    const isCurrentRow = row.userCount === numberOfMembers;
                    const prevRow = index > 0 ? pricingRows[index - 1] : null;
                    const showTierBoundary = isTierBoundary(
                      row.userCount,
                      prevRow?.userCount ?? null,
                    );

                    return (
                      <StyledTableRow
                        key={row.userCount}
                        $isCurrentRow={isCurrentRow}
                        $isTierBoundary={showTierBoundary}
                      >
                        <TableCell align="center" style={{ fontWeight: 600 }}>
                          {isCurrentRow ? (
                            <Chip
                              label={row.userCount.toLocaleString()}
                              size="small"
                              color="primary"
                              sx={{
                                height: 24,
                                fontSize: '0.8rem',
                                fontWeight: 700,
                                minWidth: 60,
                              }}
                            />
                          ) : (
                            row.userCount.toLocaleString()
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={`Platform Fee Tier ${row.platformTier}`}>
                            <Chip
                              label={`T${row.platformTier}`}
                              size="small"
                              variant="outlined"
                              color="default"
                              sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                fontWeight: 500,
                              }}
                            />
                          </Tooltip>
                        </TableCell>
                        <NumericCell>{formatPrice(row.pricePerSeat)}</NumericCell>
                        <NumericCell>{formatCurrency(row.recurringSubscriptionFee)}</NumericCell>
                        <NumericCell>{formatCurrency(row.recurringAnnualPlatformFee)}</NumericCell>
                        {securityPackageSso && (
                          <NumericCell>{formatCurrency(row.recurringSsoAnnualFee)}</NumericCell>
                        )}
                        <NumericCell>{formatCurrency(row.recurringIntegrationFee)}</NumericCell>
                        {directDatabaseIntegration && !isDedicatedTenant(tenantType) && (
                          <NumericCell>
                            {formatCurrency(row.recurringDirectDatabaseFee)}
                          </NumericCell>
                        )}
                        {isolatedDatabase &&
                          !directDatabaseIntegration &&
                          !isDedicatedTenant(tenantType) && (
                            <NumericCell>
                              {formatCurrency(row.recurringIsolatedDatabaseFee)}
                            </NumericCell>
                          )}
                        {isDedicatedTenant(tenantType) && (
                          <NumericCell>{formatCurrency(row.recurringTenantFee)}</NumericCell>
                        )}
                        {govCloud && (
                          <NumericCell>{formatCurrency(row.recurringGovCloudFee)}</NumericCell>
                        )}
                        {(aiForecasting ||
                          nonPeopleResources ||
                          profileBuilding ||
                          skillsTagging ||
                          solutionsSupport ||
                          enableWhiteLabel) && (
                          <NumericCell>{formatCurrency(row.recurringAddonFee)}</NumericCell>
                        )}
                        <NumericCell style={{ fontWeight: 600 }}>
                          {formatCurrency(row.totalRecurringFee)}
                        </NumericCell>
                        <NumericCell
                          sx={{
                            backgroundColor: alpha(theme.palette.success.main, 0.05),
                            fontWeight: 600,
                            color: theme.palette.success.main,
                          }}
                        >
                          {formatPrice(row.costPerUser)}
                        </NumericCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </Paper>
          <ResultsText variant="body2">
            {pricingRows.length} results
            {pricingRows.length >= MAX_ROWS && ' (limited to 300 rows)'}
          </ResultsText>
        </>
      ) : (
        <Paper elevation={1} style={{ padding: '32px', textAlign: 'center' }}>
          <Typography color="primary">
            Please enter valid increment and maximum values to generate the pricing table.
          </Typography>
        </Paper>
      )}
    </ScaleTableContainer>
  );
};
